import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, ArrowLeft, CheckCircle, Search } from 'lucide-react';
import { useAuthStore } from '@/stores';
import { authApi } from '@/services/api';
import { Input } from '@/components/ui';
import { useNotification } from '@/hooks';
import { useValidationRules, useAppInfo, useFeatureFlags } from '@/contexts';

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuthStore();
  const notification = useNotification();
  const validationRules = useValidationRules();
  const appInfo = useAppInfo();
  const { enableRegistration } = useFeatureFlags();

  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [agreed, setAgreed] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.username.trim()) newErrors.username = '请输入用户名';
    else if (formData.username.length < validationRules.USERNAME_MIN_LENGTH) newErrors.username = `用户名至少${validationRules.USERNAME_MIN_LENGTH}个字符`;
    else if (formData.username.length > validationRules.USERNAME_MAX_LENGTH) newErrors.username = `用户名最多${validationRules.USERNAME_MAX_LENGTH}个字符`;
    else if (!validationRules.USERNAME_REGEX.test(formData.username)) newErrors.username = '用户名只能包含字母、数字和下划线';
    if (!formData.email.trim()) newErrors.email = '请输入邮箱';
    else if (!validationRules.EMAIL_REGEX.test(formData.email)) newErrors.email = '请输入有效的邮箱地址';
    if (!formData.password) newErrors.password = '请输入密码';
    else if (formData.password.length < validationRules.PASSWORD_MIN_LENGTH) newErrors.password = `密码至少${validationRules.PASSWORD_MIN_LENGTH}个字符`;
    else if (formData.password.length > validationRules.PASSWORD_MAX_LENGTH) newErrors.password = `密码最多${validationRules.PASSWORD_MAX_LENGTH}个字符`;
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = '两次密码输入不一致';
    if (!agreed) notification.common.validationError('服务条款');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && agreed;
  };

  const handleRegister = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const response = await authApi.register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
      });
      if (response.success && response.data) {
        setUser(response.data.user);
        setToken(response.data.token);
        setSuccess(true);
        notification.auth.registerSuccess();
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        notification.auth.registerFailed(response.message || '注册失败');
      }
    } catch (error: any) {
      notification.auth.registerFailed(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleRegister();
  };

  if (!enableRegistration) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-slate-50 dark:bg-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-400/6 dark:bg-blue-500/5 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-violet-400/6 dark:bg-violet-500/4 rounded-full blur-[100px]" />
          <div className="absolute inset-0 grid-dots opacity-50" />
        </div>

        <div className="w-full max-w-md relative">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200 mb-8 transition-colors text-sm font-medium group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            返回首页
          </Link>

          <div className="rounded-2xl bg-white dark:bg-slate-900/80 border border-slate-200/80 dark:border-slate-700/50 p-7 sm:p-8 text-center"
            style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)' }}>
            <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <Lock className="w-6 h-6 text-slate-400" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">注册功能暂未开放</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">管理员尚未开放注册功能，请稍后再试</p>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/')}
                className="flex-1 py-2.5 px-4 rounded-xl font-medium text-sm border-2 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-blue-300 dark:hover:border-blue-600 transition-all"
              >
                返回首页
              </button>
              <button
                onClick={() => navigate('/login')}
                className="flex-1 py-2.5 px-4 rounded-xl font-semibold text-sm text-white btn-gradient"
              >
                前往登录
              </button>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-slate-400 dark:text-slate-600">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center">
              <Search className="w-3 h-3 text-white" />
            </div>
            <span className="text-xs font-medium">{appInfo.NAME}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-slate-50 dark:bg-slate-950 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-400/6 dark:bg-blue-500/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-violet-400/6 dark:bg-violet-500/4 rounded-full blur-[100px]" />
        <div className="absolute inset-0 grid-dots opacity-50" />
      </div>

      <div className="w-full max-w-md relative">

        <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200 mb-8 transition-colors text-sm font-medium group">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          返回首页
        </Link>

        <div className="rounded-2xl bg-white dark:bg-slate-900/80 border border-slate-200/80 dark:border-slate-700/50 p-7 sm:p-8"
          style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)' }}>

          {success ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h1 className="display-font text-xl font-bold text-slate-900 dark:text-slate-100 mb-2 tracking-tight">注册成功！</h1>
              <p className="text-slate-600 dark:text-slate-400 mb-6">欢迎加入{appInfo.NAME}，即将跳转到控制台...</p>
              <div className="flex items-center justify-center gap-2">
                {[0, 150, 300].map((delay, i) => (
                  <div key={i} className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: `${delay}ms` }} />
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="text-center mb-7">
                <h1 className="display-font text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight mb-1.5">
                  创建账号
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">注册即可享受更多功能</p>
              </div>

              <div className="accent-line mb-6" />

              <form onSubmit={(e) => { e.preventDefault(); handleRegister(); }} className="space-y-4" onKeyDown={handleKeyDown}>
                <Input
                  label="用户名"
                  type="text"
                  placeholder="请输入用户名"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  error={errors.username}
                  leftIcon={<User className="w-5 h-5" />}
                  fullWidth
                />
                <Input
                  label="邮箱"
                  type="email"
                  placeholder="请输入邮箱"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  error={errors.email}
                  leftIcon={<Mail className="w-5 h-5" />}
                  fullWidth
                />
                <Input
                  label="密码"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="请输入密码"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  error={errors.password}
                  leftIcon={<Lock className="w-5 h-5" />}
                  rightIcon={
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  }
                  fullWidth
                />
                <Input
                  label="确认密码"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="请再次输入密码"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  error={errors.confirmPassword}
                  leftIcon={<Lock className="w-5 h-5" />}
                  fullWidth
                />

                <label className="flex items-start gap-3 cursor-pointer select-none py-1">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5 rounded border-slate-300 dark:border-slate-600 text-blue-500 focus:ring-blue-500 w-4 h-4 flex-shrink-0"
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                    我已阅读并同意{' '}
                    <Link to="/terms" className="text-blue-600 dark:text-blue-400 hover:underline">服务条款</Link>
                    {' '}和{' '}
                    <Link to="/privacy" className="text-blue-600 dark:text-blue-400 hover:underline">隐私政策</Link>
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-semibold text-white btn-gradient disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoading
                    ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />注册中...</>
                    : '立即注册'
                  }
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  已有账号？{' '}
                  <Link to="/login" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-semibold transition-colors">
                    立即登录
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 text-slate-400 dark:text-slate-600">
          <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center">
            <Search className="w-3 h-3 text-white" />
          </div>
          <span className="text-xs font-medium">{appInfo.NAME}</span>
        </div>
      </div>
    </div>
  );
};
